import React, { useState, useEffect } from 'react';

const LoginModal = ({ isOpen, setIsOpen, onLogin }) => {
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [errors, setErrors] = useState({});

    // Close modal with Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [setIsOpen, isOpen]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            // Pass both username and password to login handler
            onLogin(formData.username, formData.password);
            setFormData({ username: '', password: '' });
            setErrors({});
            setIsOpen(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors({ ...errors, [field]: '' });
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            setIsOpen(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 login-modal-overlay"
            id="login-modal-overlay"
            onClick={handleOverlayClick}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <div className="bg-white rounded-lg p-6 w-96 max-w-lg login-modal-content mx-auto" id="login-modal-content" data-test-id="login-modal-content">
                <div className="login-modal-header mb-4">
                    <h3 className="text-xl font-bold login-modal-title text-center" id="login-modal-title" data-test-id="login-modal-title">
                        Login to Your Account
                    </h3>
                </div>

                <div className="login-modal-body" data-test-id="login-modal-body">
                    <div className="space-y-4 login-form" id="login-form">
                        {/* Username field */}
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 label" htmlFor="login-username" data-test-id="username-label">
                                Username
                            </label>
                            {errors.username && (
                                <div className="validation-error text-red-600 text-sm mt-1" id="username-error" data-test-id="username-error">
                                    {errors.username}
                                </div>
                            )}
                            <input
                                type="text"
                                id="login-username"
                                className={`mt-1 block w-full border rounded-md px-3 py-2 input-field ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
                                value={formData.username}
                                onChange={(e) => handleInputChange('username', e.target.value)}
                                placeholder="Enter your username"
                                data-test-id="username-input"
                            />
                        </div>

                        {/* Password field */}
                        <div className="form-group">
                            <label className="block text-sm font-medium text-gray-700 label" htmlFor="login-password" data-test-id="password-label">
                                Password
                            </label>
                            {errors.password && (
                                <div className="validation-error text-red-600 text-sm mt-1" id="password-error" data-test-id="password-error">
                                    {errors.password}
                                </div>
                            )}
                            <input
                                type="password"
                                id="login-password"
                                className={`mt-1 block w-full border rounded-md px-3 py-2 input-field ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                placeholder="Enter your password"
                                data-test-id="password-input"
                            />
                        </div>
                    </div>
                </div>

                <div className="login-modal-footer flex justify-end space-x-2 mt-6">
                    <button
                        type="button"
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 btn-cancel"
                        id="login-cancel-btn"
                        data-test-id="login-cancel-button"
                        onClick={() => setIsOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded btn-login"
                        id="login-submit-btn"
                        data-test-id="login-submit-button"
                        onClick={handleSubmit}
                    >
                        Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;